import moment from 'moment'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import { useFieldValidation } from './config'

import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import IconButton from 'LuxKit/Button/IconButton'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import TripContext from 'tripPlanner/contexts/TripContext'
import { getCalendarMinDate } from 'tripPlanner/utils/calendarMinDate'

const Day = styled(Clickable)`
  min-height: ${rem(56)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.inRange {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.inThePast:not(.selected, .inRange) {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    cursor: not-allowed;
  }

  ${mediaHoverable} {
    &:not(:disabled):hover {
      filter: brightness(0.95);
    }
  }
  &:focus-visible {
    ${focusStyle}
  }
`

interface Props
  extends Omit<React.ComponentProps<typeof DatePickerInput>, 'onChange'> {
  name: string
}

function RHFDatePickerInput(props: Props) {
  const { noValidationSpacing = true, onDateChange, disabled, min, ...rest } = props
  const intl = useIntl()
  const { required } = useFieldValidation()
  const { control, setValue, setError, watch } = useFormContext()
  const { startDate: tripStartDate, endDate: tripEndDate } = useContext(TripContext) ?? {}

  const date = watch(props.name)

  // Grey out past days, but never later than this field's own current value so
  // an item that already sits in the past stays editable. A caller-supplied
  // `min` wins; either way it is normalised to the start of the day so the
  // boundary day itself remains selectable.
  const effectiveMin = useMemo(() => {
    if (min) {
      return moment(min).startOf('day').toDate()
    }
    const currentValue = date ? moment(date, ISO_DATE_FORMAT) : undefined
    return getCalendarMinDate(currentValue).toDate()
  }, [min, date])

  // Clearing the value doesn't properly update DatePickerInput and its underlying DateInput,
  // because of crazy internal state stuff and the subtleties of null vs. undefined values.
  // As a workaround, change the key to replace the entire component instance.
  // This is awful, but changing the way those components work is too difficult since a bunch
  // of stuff depends on them that I don't want to test.
  const [inputKey, setInputKey] = useState(0)

  const {
    field: { onChange, ...controllerProps },
    fieldState: { error },
  } = useController({
    name: props.name,
    control,
    rules: {
      required: props.required && required,
    },
  })

  const handleDateChange = useCallback(
    (val?: Date) => {
      if (val) {
        setValue(props.name, moment(val).format(ISO_DATE_FORMAT), {
          shouldDirty: true,
          shouldValidate: true,
        })
      } else if (!props.required) {
        setValue(props.name, null, {
          shouldDirty: true,
          shouldValidate: true,
        })
        setInputKey((key) => key + 1)
      }
      onDateChange?.(val)
    },
    [props.required, props.name, onDateChange, setValue],
  )

  const getCustomError = useCallback(
    (msg: string) => {
      setError(props.name, {
        message: msg,
        type: 'pattern',
      })
    },
    [props.name, setError],
  )

  const clear: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> =
    useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
        setValue(props.name, null, {
          shouldTouch: true,
          shouldValidate: true,
        })
        setInputKey((key) => key + 1)
      },
      [setValue, props.name],
    )

  useEffect(() => {
    if (!error) {
      setInputKey((key) => key + 1)
    }
  }, [error])

  const tripStartsLabel = intl.formatMessage({
    id: 'xZTytu0pOo06b_mipeY1',
    defaultMessage: 'Trip starts',
    description: 'Label shown on calendar day when the trip starts in the trip planner item editor modal',
  })

  const tripEndsLabel = intl.formatMessage({
    id: 'R6GpgqXYXqxnyygBG9QH',
    defaultMessage: 'Trip ends',
    description: 'Label shown on calendar day when the trip ends in the trip planner item editor modal',
  })

  const selectDatePlaceholder = intl.formatMessage({
    id: 'REzXRXGBG7tghjyIaKqi',
    defaultMessage: 'Select date',
    description: 'Placeholder text for date picker input in trip item form',
  })

  const dayRender = useCallback<Exclude<React.ComponentProps<typeof DatePickerInput>['dayRender'], undefined>>((day, { selected, onDayClick, disabled: isDayDisabled }) => {
    const dayMoment = moment(day)

    let label: React.ReactNode | undefined
    if (tripStartDate && dayMoment.isSame(tripStartDate, 'day')) {
      label = tripStartsLabel
    } else if (tripEndDate && dayMoment.isSame(tripEndDate, 'day')) {
      label = tripEndsLabel
    } else {
      label = <>&nbsp;</>
    }

    return <Day
      key={day.getTime()}
      disabled={isDayDisabled}
      className={clsx({ selected, inThePast: isDayDisabled })}
      onClick={() => onDayClick?.(day)}
    >
      <BodyText
        variant="medium"
        weight="bold"
        colour={isDayDisabled ? 'light-foreground-alt' : undefined}
        data-date={dayMoment.format(ISO_DATE_FORMAT)}
      >
        {dayMoment.date()}
      </BodyText>
      <Caption
        variant="medium"
        lineClamp={2}
        wrap="word-wrap"
        colour={isDayDisabled ? 'light-foreground-alt' : 'highlight-primary-dark'}
      >
        {label}
      </Caption>
    </Day>
  }, [tripEndDate, tripEndsLabel, tripStartDate, tripStartsLabel])

  return (
    <DatePickerInput
      {...rest}
      {...controllerProps}
      min={effectiveMin}
      placeholder={selectDatePlaceholder}
      manualError={!!error?.message}
      disabled={disabled}
      key={inputKey}
      noValidationSpacing={noValidationSpacing}
      required={props.required}
      startIcon={<LineCalendarIcon />}
      endIcon={
        date &&
        !disabled &&
        !props.required && (
          <IconButton kind="tertiary" variant="ghost" onClick={clear}>
            <LineTimesIcon colour="normal-contrast" />
          </IconButton>
        )
      }
      onDateChange={handleDateChange}
      getCustomError={getCustomError}
      invalidErrorMessage={error?.message}
      displayFormat={DMY_CASUAL_SHORT_FORMAT}
      dayRender={dayRender}
    />
  )
}

export default RHFDatePickerInput
